import { forwardRef, useId, useImperativeHandle, useRef, useState, type KeyboardEvent } from "react";
import { X } from "lucide-react";

import { Label } from "@/components/ui/Input";
import { cn } from "@/lib/utils";
import {
  commitDraftTextChip,
  mergeTextChips,
  parseTextChipTokens,
} from "@/lib/textChipInput";

export type TextChipInputHandle = {
  flushDraft: () => string[];
};

export const TextChipInput = forwardRef(function TextChipInput({
  id: providedId,
  label,
  chips,
  onChipsChange,
  placeholder = "Type a value and press Enter",
  disabled = false,
  className,
  onDraftChange,
}: {
  id?: string;
  label: string;
  chips: string[];
  onChipsChange: (chips: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  onDraftChange?: (draft: string) => void;
}, ref): JSX.Element {
  const generatedId = useId();
  const inputId = providedId ?? generatedId;
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState("");

  function focusInput(): void {
    inputRef.current?.focus();
  }

  function commitDraft(): string[] {
    const { chip, remainingDraft } = commitDraftTextChip(draft);
    const nextChips = chip ? mergeTextChips(chips, [chip]) : chips;
    if (chip) {
      onChipsChange(nextChips);
    }
    setDraft(remainingDraft);
    onDraftChange?.(remainingDraft);
    return nextChips;
  }

  function updateDraft(nextDraft: string): void {
    setDraft(nextDraft);
    onDraftChange?.(nextDraft);
  }

  useImperativeHandle(ref, () => ({
    flushDraft: () => commitDraft(),
  }));

  function removeChip(chip: string): void {
    onChipsChange(chips.filter((value) => value !== chip));
    focusInput();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "Backspace" && draft.length === 0 && chips.length > 0) {
      onChipsChange(chips.slice(0, -1));
      return;
    }

    if (event.key === "Enter" || event.key === "Tab" || event.key === "," || event.key === ";") {
      if (draft.trim().length > 0) {
        event.preventDefault();
        commitDraft();
      }
    }
  }

  function handlePaste(text: string): void {
    const parsed = parseTextChipTokens(text);
    if (parsed.length === 0) {
      return;
    }
    onChipsChange(mergeTextChips(chips, parsed));
    updateDraft("");
  }

  return (
    <div className={className}>
      <Label htmlFor={inputId}>{label}</Label>
      <div
        className={cn(
          "mt-1 flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500",
          disabled && "cursor-not-allowed bg-slate-100",
        )}
        onClick={() => {
          if (!disabled) {
            focusInput();
          }
        }}
      >
        {chips.map((chip) => (
          <span
            key={chip}
            className="inline-flex max-w-full items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-800"
          >
            <span className="truncate" title={chip}>{chip}</span>
            <button
              type="button"
              className="rounded p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800 disabled:pointer-events-none"
              aria-label={`Remove ${chip}`}
              disabled={disabled}
              onClick={(event) => {
                event.stopPropagation();
                removeChip(chip);
              }}
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          autoComplete="off"
          disabled={disabled}
          value={draft}
          placeholder={chips.length === 0 ? placeholder : ""}
          className="min-w-[8rem] flex-1 border-0 bg-transparent px-1 py-1 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none disabled:cursor-not-allowed"
          onChange={(event) => {
            const nextValue = event.target.value;
            if (/[,;]/.test(nextValue)) {
              const committed = parseTextChipTokens(nextValue);
              if (committed.length > 0) {
                onChipsChange(mergeTextChips(chips, committed));
                updateDraft("");
                return;
              }
            }
            updateDraft(nextValue);
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => commitDraft()}
          onPaste={(event) => {
            const text = event.clipboardData.getData("text");
            if (!/[,;\n]/.test(text)) {
              return;
            }
            event.preventDefault();
            handlePaste(text);
          }}
        />
      </div>
    </div>
  );
});
TextChipInput.displayName = "TextChipInput";
